/**
 * Diálogo de una acción de servidor: confirmación y parámetros.
 *
 * El backend declara con `ActionParam` los datos que la acción le pide al
 * usuario —el `motivo` de una baja, el `porcentaje` de un ajuste de precio, el
 * rango del libro de IVA— y los valida con `coerce()` antes de correr: si falta
 * uno obligatorio contesta 400. Disparar la acción sin pedirlos, que es lo que
 * hacía la botonera, convertía nueve acciones del catálogo en un error seguro.
 *
 * Es el mismo diálogo para los dos casos y no dos componentes, porque son el
 * mismo momento: "antes de correr esto, ¿está todo dicho?". Una acción sin
 * parámetros y sin `confirm` no lo abre en absoluto — confirmar todo entrena al
 * usuario a decir que sí sin leer.
 *
 * Los controles son de HTML plano y no los widgets de `components/fields`: un
 * `ActionParam` no es un `FieldSpec` (no tiene `store` ni decoraciones), y los
 * tipos escalares que el backend admite se dibujan con un `input`. Hay tres
 * excepciones, las tres por la misma razón —el control casero era peor que el
 * del ERP, o directamente no existía—:
 *
 * - la **fecha** usa el `DatePickerInput`: el `<input type="date">` nativo se
 *   dibuja con el formato del sistema operativo y hacía que «Fecha de las
 *   facturas» saliera mm/dd/yyyy en una pantalla donde todo lo demás va
 *   dd/mm/aaaa;
 * - el **many2one** usa el `RelationPicker`, el mismo buscador por nombre que
 *   los formularios. El parámetro declara su `relation` y lo que viaja es el id;
 *   pedirlo como número suelto habría obligado a la persona a saber de memoria
 *   el id de «1.1.02 Banco cuenta corriente»;
 * - el `char` con **`choices`** se dibuja como `<select>`. Mismo motivo: lo que
 *   se guarda es un código interno («CABA») y lo que una persona reconoce es la
 *   etiqueta («Ciudad Autónoma de Buenos Aires»).
 */

import { useEffect, useRef, useState } from 'react';

import { DatePickerInput } from '@/components/fields/DatePickerInput';
import { EntradaDeClave } from '@/components/fields/PasswordField';
import { isMany2One } from '@/components/fields/format';
import { RelationPicker } from '@/components/fields/RelationPicker';
import { Button } from '@/components/shell/primitives';
import type { ActionParam, JsonValue, Many2OneValue, ModelAction } from '@/lib/types';

/** Valores del formulario, tal como se mandan en el cuerpo del POST. */
export type ActionParamValues = Record<string, JsonValue>;

/** `true` si la acción necesita pasar por el diálogo antes de ejecutarse. */
export function needsDialog(action: ModelAction): boolean {
  return action.confirm === true || (action.params ?? []).length > 0;
}

/** Valores iniciales: el `default` que declaró cada parámetro. */
export function initialParamValues(action: ModelAction): ActionParamValues {
  const salida: ActionParamValues = {};
  for (const param of action.params ?? []) {
    salida[param.name] = param.default ?? (param.type === 'boolean' ? false : null);
  }
  return salida;
}

/** Lo que falta para poder disparar: nombre del parámetro → mensaje. */
function faltantes(action: ModelAction, valores: ActionParamValues): Record<string, string> {
  const problemas: Record<string, string> = {};
  for (const param of action.params ?? []) {
    if (!param.required) continue;
    const valor = valores[param.name];
    const vacio = valor === null || valor === undefined || (typeof valor === 'string' && valor.trim() === '');
    if (vacio) problemas[param.name] = 'Este dato es obligatorio.';
  }
  return problemas;
}

/**
 * Solo se envían los parámetros con valor: uno opcional que no se manda toma el
 * `default` del backend, y mandarle `null` encima sería pisarlo con nada.
 */
export function cleanParamValues(action: ModelAction, valores: ActionParamValues): ActionParamValues {
  const salida: ActionParamValues = {};
  for (const param of action.params ?? []) {
    const valor = valores[param.name];
    if (valor === null || valor === undefined) continue;
    if (typeof valor === 'string' && valor.trim() === '') continue;
    salida[param.name] = valor;
  }
  return salida;
}

export interface ActionDialogProps {
  /** Acción a ejecutar, o `null` para no dibujar nada. */
  action: ModelAction | null;
  /** Cuántos registros va a tocar: lo dice el encabezado. */
  count: number;
  loading?: boolean;
  /**
   * Por qué el servidor rechazó la acción, si ya se intentó.
   *
   * Antes esto no existía y el diálogo **se cerraba** al fallar: la persona
   * perdía de vista el motivo (quedaba en un aviso flotante) y encima perdía los
   * parámetros que acababa de tipear —el motivo de la baja, la fecha de las
   * facturas— y tenía que volver a abrirlo y escribir todo de nuevo.
   */
  error?: string | null;
  /** Lo que el 400 dijo de un PARÁMETRO concreto: `{nombre: mensaje}`. */
  fieldErrors?: Record<string, string>;
  onConfirm: (params: ActionParamValues) => void;
  onCancel: () => void;
}

export function ActionDialog({
  action,
  count,
  loading = false,
  error = null,
  fieldErrors = {},
  onConfirm,
  onCancel,
}: ActionDialogProps) {
  const panel = useRef<HTMLDivElement>(null);
  const focoPrevio = useRef<HTMLElement | null>(null);
  const [valores, setValores] = useState<ActionParamValues>({});
  const [errores, setErrores] = useState<Record<string, string>>({});

  const abierto = action !== null;

  useEffect(() => {
    if (action === null) return;
    setValores(initialParamValues(action));
    setErrores({});
  }, [action]);

  useEffect(() => {
    if (!abierto) return;
    focoPrevio.current = document.activeElement as HTMLElement | null;

    const alTeclear = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') {
        evento.stopPropagation();
        onCancel();
      }
    };
    document.addEventListener('keydown', alTeclear, true);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // El foco arranca en el primer parámetro; si no hay, en Cancelar: la tecla
    // Enter reflejo no puede disparar una acción destructiva.
    const temporizador = window.setTimeout(() => {
      const primero = panel.current?.querySelector<HTMLElement>('[data-param-control]');
      (primero ?? panel.current?.querySelector<HTMLElement>('#accion-cancelar'))?.focus();
    }, 0);

    return () => {
      document.removeEventListener('keydown', alTeclear, true);
      document.body.style.overflow = overflow;
      window.clearTimeout(temporizador);
      focoPrevio.current?.focus();
    };
  }, [abierto, onCancel]);

  if (action === null) return null;

  const params = action.params ?? [];
  const destructiva = action.destructive === true;
  const mensaje =
    action.confirm_message !== undefined && action.confirm_message !== ''
      ? action.confirm_message
      : action.help !== undefined && action.help !== ''
        ? action.help
        : params.length > 0
          ? 'Completá los datos que pide esta acción.'
          : `Se va a ejecutar sobre ${count === 1 ? '1 registro' : `${count} registros`}.`;

  /**
   * Los errores que se pintan en los controles: los que puso esta pantalla y
   * los que mandó el servidor por parámetro. Los del servidor van encima porque
   * son los del último intento.
   */
  const erroresVisibles: Record<string, string> = { ...errores, ...fieldErrors };

  const enviar = (): void => {
    const problemas = faltantes(action, valores);
    setErrores(problemas);
    const primero = Object.keys(problemas)[0];
    if (primero !== undefined) {
      // El foco va al parámetro que falta. Sin esto, apretar el botón de una
      // acción con tres parámetros dejaba un mensaje chiquito en rojo abajo del
      // control y nada más: en un diálogo con scroll, fuera de la vista.
      panel.current?.querySelector<HTMLElement>(`#param-${CSS.escape(primero)}`)?.focus();
      return;
    }
    onConfirm(cleanParamValues(action, valores));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center">
      <div className="absolute inset-0 bg-fg/30 animate-fade-in" onClick={onCancel} aria-hidden="true" />
      {/*
        Es un `div` y no un `form`: la botonera vive DENTRO del `<form>` del
        formulario de registro, y un form anidado es HTML invalido — el submit
        del dialogo terminaba disparando el guardado del registro en vez de la
        accion. Enter se maneja a mano sobre los controles.
      */}
      <div
        ref={panel}
        role={destructiva ? 'alertdialog' : 'dialog'}
        aria-modal="true"
        aria-labelledby="accion-titulo"
        aria-describedby={error === null ? 'accion-texto' : 'accion-texto accion-error'}
        onKeyDown={(evento) => {
          if (evento.key === 'Enter' && (evento.target as HTMLElement).tagName !== 'TEXTAREA') {
            evento.preventDefault();
            enviar();
          }
        }}
        className="relative w-full max-w-md rounded-xl border border-line bg-surface p-5 shadow-lg animate-slide-up"
      >
        <h2 id="accion-titulo" className="text-lg font-semibold">
          {action.label}
        </h2>
        <p id="accion-texto" className="mt-2 text-sm text-fg-muted">
          {mensaje}
        </p>
        {count > 1 && (
          <p className="mt-1 text-sm text-fg-faint">Se aplica a {count} registros seleccionados.</p>
        )}

        {/* Por qué el servidor la rechazó. Fijo, dentro del diálogo y arriba de
            los parámetros, que es donde mira quien acaba de apretar el botón. */}
        {error !== null && (
          <p
            id="accion-error"
            role="alert"
            className="mt-4 rounded-lg border border-danger bg-danger-soft px-3 py-2.5 text-sm text-fg"
          >
            <span className="font-medium">No se pudo. </span>
            {error}
          </p>
        )}

        {params.length > 0 && (
          <div className="mt-4">
            <ParamsDeLaAccion
              params={params}
              valorDe={(nombre) => valores[nombre] ?? null}
              errorDe={(nombre) => erroresVisibles[nombre] ?? null}
              onChange={(nombre, valor) => {
                setValores((actual) => ({ ...actual, [nombre]: valor }));
                setErrores((actual) => {
                  if (actual[nombre] === undefined) return actual;
                  const copia = { ...actual };
                  delete copia[nombre];
                  return copia;
                });
              }}
            />
          </div>
        )}

        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={onCancel} id="accion-cancelar">
            Cancelar
          </Button>
          <Button variant={destructiva ? 'danger' : 'primary'} loading={loading} onClick={enviar}>
            {action.label}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Control de un parámetro
// ---------------------------------------------------------------------------
const CLASE_CAMPO =
  'h-10 w-full rounded-md border border-line bg-surface px-3 text-sm outline-none transition-colors ' +
  'focus:border-accent focus:ring-2 focus:ring-accent/25';

/**
 * TODOS los parámetros de una acción, agrupados como los declaró el backend.
 *
 * Se exporta porque el diálogo no es el único lugar que los dibuja: el
 * desplegable «¿Qué es este contacto?» los pide adentro del formulario del
 * contacto (`TipoDeContactoField`). Que los dos usen esta función es lo que hace
 * que agrupar sea un cambio y no dos, y —más importante— que las dos pantallas
 * no puedan mostrar el mismo formulario de dos maneras distintas.
 *
 * POR QUÉ AGRUPAR. Mientras las acciones pedían dos o tres datos, una pila
 * alcanzaba. El alta de una administración desde el contacto pide VEINTE, y ahí
 * la pila no sólo es larga: es ambigua, porque las etiquetas se repiten
 * literalmente («Calle», «Número», «Piso», «Depto.» y «Código postal» aparecen
 * dos veces cada una, la oficina de CABA y la de Provincia).
 *
 * SIN NINGÚN `grupo` DECLARADO DIBUJA EXACTAMENTE LO DE ANTES, y es a propósito:
 * todas las acciones que ya existían siguen viéndose igual, así que esto no le
 * cambia la cara a ninguna pantalla que nadie pidió cambiar.
 *
 * El orden no se toca: los bloques salen en el orden en que aparece su primer
 * parámetro, y adentro de cada uno los parámetros quedan como los declaró la
 * acción. Reordenar acá sería que el backend declare una cosa y se vea otra.
 */
export function ParamsDeLaAccion({
  params,
  valorDe,
  errorDe,
  onChange,
}: {
  params: ActionParam[];
  valorDe: (nombre: string) => JsonValue;
  errorDe: (nombre: string) => string | null;
  onChange: (nombre: string, valor: JsonValue) => void;
}) {
  const control = (param: ActionParam) => (
    <ActionParamControl
      key={param.name}
      param={param}
      valor={valorDe(param.name)}
      error={errorDe(param.name)}
      onChange={(valor) => {
        onChange(param.name, valor);
      }}
    />
  );

  if (!params.some((param) => (param.grupo ?? '') !== '')) {
    return <div className="space-y-4">{params.map(control)}</div>;
  }

  const bloques: { grupo: string; items: ActionParam[] }[] = [];
  for (const param of params) {
    const grupo = param.grupo ?? '';
    const ultimo = bloques[bloques.length - 1];
    if (ultimo !== undefined && ultimo.grupo === grupo) ultimo.items.push(param);
    else bloques.push({ grupo, items: [param] });
  }

  return (
    <div className="space-y-4">
      {bloques.map((bloque, indice) =>
        bloque.grupo === '' ? (
          <div key={`suelto-${indice}`} className="space-y-4">
            {bloque.items.map(control)}
          </div>
        ) : (
          // `fieldset`/`legend` y no un `div` con un `<p>` en negrita: es la
          // forma que un lector de pantalla anuncia como «grupo», y es
          // justamente lo que hace que «Calle» de la oficina de Provincia no
          // suene igual que «Calle» de la de CABA para quien no ve la pantalla.
          <fieldset
            key={`${bloque.grupo}-${indice}`}
            className="rounded-md border border-line px-3 pb-3 pt-1"
          >
            <legend className="px-1 text-xs font-medium text-fg-muted">{bloque.grupo}</legend>
            <div className="space-y-4">{bloque.items.map(control)}</div>
          </fieldset>
        ),
      )}
    </div>
  );
}

/**
 * El control de UN parámetro declarado.
 *
 * Se exporta porque el diálogo no es el único lugar donde se piden parámetros:
 * la pantalla de reporte (`ReportScreen`) los pide en línea, arriba del cuadro.
 * Que los dos usen este control es lo que hace que un `ActionParam` de tipo
 * fecha se vea igual en los dos lados —y que agregar un tipo nuevo sea un solo
 * cambio y no dos.
 */
export function ActionParamControl({
  param,
  valor,
  error,
  onChange,
}: {
  param: ActionParam;
  valor: JsonValue;
  error: string | null;
  onChange: (valor: JsonValue) => void;
}) {
  const id = `param-${param.name}`;
  const idAyuda = `${id}-ayuda`;
  const idError = `${id}-error`;
  const descrito = [param.help !== '' ? idAyuda : null, error !== null ? idError : null]
    .filter((parte) => parte !== null)
    .join(' ');

  const comunes = {
    id,
    name: param.name,
    'data-param-control': true,
    'aria-invalid': error !== null,
    ...(descrito === '' ? {} : { 'aria-describedby': descrito }),
  } as const;

  return (
    <div>
      {param.type === 'boolean' ? (
        <label className="flex items-start gap-2.5 text-sm">
          <input
            {...comunes}
            type="checkbox"
            checked={valor === true}
            onChange={(evento) => {
              onChange(evento.target.checked);
            }}
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-line accent-[rgb(var(--accent))]"
          />
          <span>
            <span className="font-medium">{param.label}</span>
            {param.help !== '' && (
              <span id={idAyuda} className="mt-0.5 block text-xs text-fg-faint">
                {param.help}
              </span>
            )}
          </span>
        </label>
      ) : (
        <>
          <label htmlFor={id} className="mb-1.5 block text-sm font-medium">
            {param.label}
            {param.required && (
              <span className="ml-1 text-danger" aria-hidden="true">
                *
              </span>
            )}
          </label>
          {param.type === 'text' ? (
            <textarea
              {...comunes}
              rows={3}
              value={typeof valor === 'string' ? valor : ''}
              onChange={(evento) => {
                onChange(evento.target.value);
              }}
              className={`${CLASE_CAMPO} h-auto py-2`}
            />
          ) : param.choices !== undefined && param.choices.length > 0 ? (
            /*
              Lista CERRADA de valores: un desplegable, no una caja de texto.

              Se decide por la FORMA del parametro y no por su nombre, igual que
              el resto de los casos especiales de este dialogo. El caso que lo
              trajo es la jurisdiccion de un consorcio nuevo: lo que se guarda es
              «CABA» y lo que una persona reconoce es «Ciudad Autonoma de Buenos
              Aires», asi que pedirlo escrito habria sido pedirle a un vendedor
              que supiera de memoria los codigos internos del sistema.

              La opcion vacia solo se ofrece cuando el parametro NO es
              obligatorio: en uno obligatorio seria una eleccion valida a la
              vista que el servidor despues rechaza.
            */
            <select
              {...comunes}
              value={typeof valor === 'string' ? valor : ''}
              onChange={(evento) => {
                const crudo = evento.target.value;
                onChange(crudo === '' ? null : crudo);
              }}
              className={CLASE_CAMPO}
            >
              {(!param.required || typeof valor !== 'string' || valor === '') && (
                <option value="">Elegí una opción…</option>
              )}
              {param.choices.map((opcion) => (
                <option key={opcion.value} value={opcion.value}>
                  {opcion.label}
                </option>
              ))}
            </select>
          ) : param.type === 'many2one' && param.relation !== undefined && param.relation !== '' ? (
            <ControlMany2One
              id={id}
              param={param}
              relation={param.relation}
              valor={valor}
              invalid={error !== null}
              {...(descrito === '' ? {} : { describedBy: descrito })}
              onChange={onChange}
            />
          ) : param.type === 'password' ? (
            /*
              UNA CLAVE GUARDADA, con la MISMA caja que el campo de la ficha
              (`EntradaDeClave`, extraida de `PasswordField`) y no una nueva.

              Sin esta rama el parametro caia en el fallback de mas abajo, que
              dibuja `type={param.type === 'char' ? 'text' : 'number'}`: o sea
              que un tipo desconocido salia como un input NUMERICO, y si alguien
              lo "arreglaba" declarandolo `char` salia como texto plano a la
              vista de cualquiera que pasara por atras. Es exactamente el defecto
              que `PasswordField` existe para evitar, reaparecido por la puerta
              de las acciones.

              Los otros dos candados no son de acá y conviene que quede escrito:
              QUIEN la ve lo decide el servidor podando `params` por grupo
              (`ActionParam.groups`), y que no viaje en la copia de demostracion
              lo decide la mascara del exportador. Un widget nunca decide
              permisos.
            */
            <EntradaDeClave
              id={id}
              name={param.name}
              data-param-control
              value={typeof valor === 'string' ? valor : ''}
              required={param.required}
              invalid={error !== null}
              {...(descrito === '' ? {} : { describedBy: descrito })}
              onChange={(texto) => {
                onChange(texto === '' ? null : texto);
              }}
            />
          ) : param.type === 'date' ? (
            /*
              El MISMO control de fecha que el resto del ERP (`DatePickerInput`),
              no un `<input type="date">` pelado. El nativo dibuja la fecha con el
              locale del SISTEMA OPERATIVO: en una maquina en ingles «Fecha de las
              facturas» salia mm/dd/yyyy mientras los vencimientos del mismo
              periodo iban en dd/mm/aaaa, y una fecha ambigua sobre una tanda de
              facturas es un error caro. Vale para TODOS los parametros de fecha
              de todas las acciones de servidor, no solo para facturar.
            */
            <DatePickerInput
              id={id}
              name={param.name}
              data-param-control
              value={typeof valor === 'string' ? valor : ''}
              onChange={(iso) => {
                onChange(iso);
              }}
              invalid={error !== null}
              required={param.required}
              {...(descrito === '' ? {} : { describedBy: descrito })}
            />
          ) : (
            <input
              {...comunes}
              type={param.type === 'char' ? 'text' : 'number'}
              {...(param.type === 'decimal' ? { step: 'any' } : {})}
              {...(param.type === 'integer' ? { step: 1 } : {})}
              value={valor === null || valor === undefined ? '' : String(valor)}
              onChange={(evento) => {
                const crudo = evento.target.value;
                if (crudo === '') {
                  onChange(null);
                  return;
                }
                // Los numéricos viajan como número; el resto como texto. El
                // backend acepta las dos formas, pero mandar `"12"` donde pide un
                // entero deja al servidor adivinando.
                if (param.type === 'integer' || param.type === 'decimal') {
                  const numero = Number(crudo);
                  onChange(Number.isNaN(numero) ? crudo : numero);
                  return;
                }
                onChange(crudo);
              }}
              className={CLASE_CAMPO}
            />
          )}
          {param.help !== '' && (
            <p id={idAyuda} className="mt-1 text-xs text-fg-faint">
              {param.help}
            </p>
          )}
        </>
      )}

      {error !== null && (
        <p id={idError} role="alert" className="mt-1 text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * El control de un parámetro `many2one`: buscar, elegir, y que se vea qué se eligió.
 *
 * El `RelationPicker` limpia su cuadro de texto al elegir —lo necesita, porque
 * en un formulario el valor elegido lo dibuja el `Many2OneField` como una
 * pastilla al lado—. Sin esa pastilla, el parámetro «Cuenta» del mayor quedaba
 * en blanco justo después de que la persona eligiera la cuenta: la única forma
 * de saber sobre qué corrió el reporte era leer el subtítulo del cuadro. Así que
 * acá se guarda el registro elegido y se lo muestra, con un botón para cambiarlo.
 *
 * Lo que viaja al backend es siempre el **id**; el nombre es sólo para los ojos.
 */
function ControlMany2One({
  id,
  param,
  relation,
  valor,
  invalid,
  describedBy,
  onChange,
}: {
  id: string;
  param: ActionParam;
  relation: string;
  valor: JsonValue;
  invalid: boolean;
  describedBy?: string;
  onChange: (valor: JsonValue) => void;
}) {
  // La pastilla puede venir puesta desde afuera: un panel que muestra lo que la
  // ficha ya tiene guardado pasa `{id, display_name}` en vez del id pelado, y
  // con el id pelado no habría nombre que dibujar.
  const [elegido, setElegido] = useState<Many2OneValue | null>(() =>
    isMany2One(valor) ? valor : null,
  );

  // Si el valor se limpia desde afuera —cambiar de reporte, resetear el
  // diálogo— la pastilla tiene que irse con él. Y si llega puesto, se pone.
  useEffect(() => {
    if (valor === null || valor === undefined) setElegido(null);
    else if (isMany2One(valor)) setElegido(valor);
  }, [valor]);

  if (elegido !== null && valor !== null) {
    return (
      <div className="flex items-center gap-1.5">
        <span className="min-w-0 flex-1 truncate rounded-md border border-line bg-app-subtle px-3 py-2 text-sm">
          {elegido.display_name}
        </span>
        <button
          type="button"
          className="shrink-0 rounded-md border border-line px-2 py-2 text-xs text-fg-muted hover:text-fg"
          onClick={() => {
            setElegido(null);
            onChange(null);
          }}
        >
          Cambiar
        </button>
      </div>
    );
  }

  return (
    <RelationPicker
      id={id}
      relation={relation}
      required={param.required}
      invalid={invalid}
      placeholder="Buscar…"
      {...(describedBy === undefined ? {} : { describedBy })}
      onPick={(registro) => {
        setElegido(registro);
        onChange(registro.id);
      }}
    />
  );
}
